import type { MouseEvent, ReactElement } from 'react';
import { boardPath, type ProfilePage, type ProfilePageBoard } from '@molt/shared';
import { ActivityEntryItem } from '@/components/card-back/ActivityEntryItem';
import { Avatar, EmptyState, Skeleton, profileName } from '@/components';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * One member's profile as everybody else reads it — identity, bio, the boards
 * the viewer may also see, and that member's recent readable activity
 * (`FR-NOTIF-033`). Presentational: props in, callbacks out, no data hooks.
 *
 * ── What this page consumes from the `FE-44.2` avatar upload contract ──────
 * `data.profile.avatarUrl` is no longer only a URL a member pasted. Since the
 * `FE-44.2` contract chunk it may be a URL **this API minted**:
 *
 *   `<WEB_ORIGIN>/api/users/:id/avatar/:avatarId` → 302 → a signed, 60-second GET
 *
 * and this file needed no change to accept it, which was the point of the
 * shape rather than a happy accident. `apps/server/src/routes/profile-avatar.ts`
 * builds that value **absolute**, through `new URL(path, config.WEB_ORIGIN)`,
 * so it is an ordinary `http(s)` URL to every reader: `avatarUrlSchema` admits
 * it, the shipped `PATCH /me/avatar` stores it, `publicProfileSchema` carries
 * it in the same `avatarUrl` field it always carried, and `Avatar` puts it in
 * the same `<img src>`. Had the contract stored a relative path, this page —
 * and every other `avatarUrl` renderer — would have had to learn a second
 * shape. `AUDIT.md` D2 is the argument; this is the consumer that proves it.
 *
 * The avatar shown here is therefore the avatar the subject **uploaded**, end
 * to end, and `apps/web/e2e/FR-NOTIF-033.spec.ts` asserts exactly that in a
 * real browser: it presigns, PUTs the bytes, patches the URL, then reads this
 * page as a *different* member and polls `naturalWidth` until the image has
 * actually decoded.
 *
 * ── This page gains no control, and that is a rule rather than an omission ──
 * `AUDIT.md` D8. The upload contract's write half (`POST /me/avatar/presign`)
 * is `requireUser` and only ever edits the **caller's own** account; this page
 * renders somebody else's. An upload affordance here could not work for the
 * subject and would not belong to the viewer. The sibling test and the browser
 * spec both pin `getByRole('button', { name: /save|edit|upload/i })` at count
 * **0** on this page — those assertions are the invariant, not scaffolding.
 * The place a member changes their own avatar is `ProfilePanel.tsx`, whose
 * docstring carries the remaining file-picker residual.
 *
 * ⚠ ── "Public" here does NOT mean anonymous, and the name invites the error ──
 * `GET /users/:id/profile-page` calls `requireUser` before it looks anything
 * up (`apps/server/src/routes/profile-page.ts`), and the route that mounts
 * this page short-circuits ahead of it: `apps/web/src/routes/users.$userId.tsx`
 * returns a sign-in `EmptyState` on `isSignedOut` and disables the query, so a
 * signed-out visitor never reaches `PublicProfilePage` at all — not even its
 * `error` branch. What they do get is `PublicProfileFrame`, the *other* export
 * of this module, which is why the frame is exported rather than private.
 * "Public" is the *audience* of the fields — the public view of a member — not
 * the reachability of the route.
 *
 * This matters because the avatar route beside it **is** anonymous-capable,
 * and a reader who assumed the two were anonymous for the same reason would
 * draw the wrong conclusion from either one. `GET /users/:id/avatar/:avatarId`
 * takes no session because `GET /boards/:id/contents`, `GET /cards/:id` and
 * `GET /cards/:id/comments` already serialise `avatarUrl` to a signed-out
 * visitor on a **public board** (`AUDIT.md` D4) — a session on the image route
 * would render every uploaded avatar broken on those surfaces, with nothing
 * red anywhere. None of that anonymity flows from this page.
 */
export interface PublicProfilePageProps {
  data: ProfilePage | null;
  loading?: boolean;
  error?: string | null;
  onOpenBoard: (path: string) => void;
  onOpenCard: (intent: BoardCardOpenIntent) => void;
}

export function PublicProfilePage({
  data,
  loading = false,
  error = null,
  onOpenBoard,
  onOpenCard,
}: PublicProfilePageProps): ReactElement {
  const { t } = useInterfaceLanguage();

  if (loading) {
    return (
      <PublicProfileFrame title={t('publicProfile.title')}>
        <div aria-label={t('publicProfile.loading')} aria-busy="true">
          <Skeleton shape="block" count={3} />
        </div>
      </PublicProfileFrame>
    );
  }

  if (error !== null || data === null) {
    return (
      <PublicProfileFrame title={t('publicProfile.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('publicProfile.loadFailedTitle')}
          body={error ?? t('publicProfile.tryAgainLater')}
        />
      </PublicProfileFrame>
    );
  }

  const name = profileName(data.profile);
  const bio = data.profile.bio?.trim() ?? '';

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-8">
      <header className="flex items-start gap-4">
        <Avatar profile={data.profile} size="md" />
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold text-ink">{name}</h1>
          {data.profile.username !== '' ? (
            <p className="text-sm text-ink-muted">@{data.profile.username}</p>
          ) : null}
          {bio !== '' ? (
            <p className="mt-3 max-w-prose whitespace-pre-wrap text-sm text-ink">{bio}</p>
          ) : null}
        </div>
      </header>

      <section aria-labelledby="profile-boards-heading" className="flex flex-col gap-2">
        <h2 id="profile-boards-heading" className="text-base font-semibold text-ink">
          {t('publicProfile.visibleBoards')}
        </h2>
        {data.boards.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('publicProfile.noSharedBoards')}</p>
        ) : (
          <ul role="list" className="grid gap-2 sm:grid-cols-2">
            {data.boards.map((board) => (
              <li key={board.id}>
                <ProfileBoardLink board={board} onOpenBoard={onOpenBoard} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="profile-activity-heading" className="flex flex-col gap-1">
        <h2 id="profile-activity-heading" className="text-base font-semibold text-ink">
          {t('publicProfile.recentActivity')}
        </h2>
        {data.recentActivity.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('publicProfile.noRecentActivity')}</p>
        ) : (
          <div>
            {data.recentActivity.map((entry) => (
              <ActivityEntryItem
                key={entry.id}
                entry={entry}
                showCard
                onOpenCard={onOpenCard}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function PublicProfileFrame({
  title,
  children,
}: {
  title: string;
  children: ReactElement;
}): ReactElement {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-8">
      <h1 className="font-display text-2xl font-semibold text-ink">{title}</h1>
      {children}
    </div>
  );
}

function ProfileBoardLink({
  board,
  onOpenBoard,
}: {
  board: ProfilePageBoard;
  onOpenBoard: (path: string) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const title =
    board.title.trim() === '' ? t('publicProfile.untitledBoard') : board.title;
  const path = boardPath(board.shortLink, board.slug);

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    onOpenBoard(path);
  }

  return (
    <a
      href={path}
      onClick={handleClick}
      className="block rounded-card border border-border-strong bg-surface-raised px-3 py-2 text-sm font-medium text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
    >
      {title}
    </a>
  );
}
